import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Pagination, Row, Table } from "react-bootstrap";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import { Loader, ScrollToTop, SearchBar } from "../../../components";

const formatTanggal = (value) =>
  value ? new Date(value).toLocaleString("id-ID") : "-";

const formatRupiah = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const getProductImage = (barang) =>
  barang?.barangfotos?.[0]?.linkGambar ||
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23f4f4f4'/%3E%3Ctext x='60' y='64' text-anchor='middle' font-family='Arial' font-size='13' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

const getTotalQty = (items = []) =>
  items.reduce((total, item) => total + Number(item.qty || 0), 0);

const getTotalHarga = (items = []) =>
  items.reduce(
    (total, item) => total + Number(item.qty || 0) * Number(item.harga || 0),
    0
  );

const TampilKeranjang = () => {
  const { user, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = location.pathname.split("/")[2];

  const [keranjangs, setKeranjangs] = useState([]);
  const [selectedKeranjang, setSelectedKeranjang] = useState(null);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        dispatch({ type: "LOGOUT" });
        navigate("/");
      }
    },
    [dispatch, navigate]
  );

  const getKeranjangs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/keranjangsPagination?search_query=${encodeURIComponent(
          searchTerm
        )}&page=${page}&limit=20`,
        {
          _id: user.id,
          token: user.token,
        }
      );
      setKeranjangs(response.data.keranjangs || []);
      setTotalPage(response.data.totalPage);
      setTotalRows(response.data.totalRows);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data keranjang.");
    }
    setLoading(false);
  }, [handleAuthError, page, searchTerm, user.id, user.token]);

  useEffect(() => {
    getKeranjangs();
  }, [getKeranjangs]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedKeranjang(null);
      return;
    }

    const foundKeranjang = keranjangs.find(
      (keranjang) => String(keranjang.id) === String(selectedId)
    );
    setSelectedKeranjang(foundKeranjang || null);
  }, [keranjangs, selectedId]);

  const searchData = (event) => {
    event.preventDefault();
    setPage(0);
    setSearchTerm(query);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Transaksi</div>
          <h1 className="page-title">Keranjang</h1>
        </div>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {selectedKeranjang && (
        <section className="section-panel">
          <div className="toolbar mt-0">
            <div className="detail-title-actions">
              <Button
                className="btn-back"
                size="sm"
                variant="outline-secondary"
                onClick={() => navigate("/keranjang")}
              >
                {"< Kembali"}
              </Button>
              <div>
                <h2 className="h6 fw-bold mb-1">Detail Keranjang</h2>
                <div className="text-muted">{selectedKeranjang.visitorId}</div>
              </div>
            </div>
          </div>
          <hr />
          <Row>
            <Col md={4}>
              <div className="mb-3">
                <div className="detail-label">Kode User Ecommerce</div>
                <div className="detail-value">{selectedKeranjang.visitorId || "-"}</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <div className="detail-label">Tanggal/Jam Dibuat</div>
                <div className="detail-value">{formatTanggal(selectedKeranjang.createdAt)}</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <div className="detail-label">Total Keranjang</div>
                <div className="detail-value">{formatRupiah(getTotalHarga(selectedKeranjang.items))}</div>
              </div>
            </Col>
          </Row>
          <Table responsive className="data-table mt-3">
            <thead>
              <tr>
                <th>Foto</th>
                <th>Barang</th>
                <th>Qty</th>
                <th>Harga</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(selectedKeranjang.items || []).map((item) => (
                <tr key={item.id}>
                  <td>
                    <img
                      src={getProductImage(item.barang)}
                      alt={item.barang?.nama || "Barang"}
                      style={{ width: 58, height: 58, objectFit: "cover", borderRadius: 6 }}
                    />
                  </td>
                  <td>{item.barang?.nama || "-"}</td>
                  <td>{item.qty}</td>
                  <td>{formatRupiah(item.harga)}</td>
                  <td>{formatRupiah(Number(item.qty || 0) * Number(item.harga || 0))}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}

      <Form onSubmit={searchData}>
        <div className="toolbar">
          <div className="d-flex gap-2 flex-wrap">
            <SearchBar setSearchTerm={setQuery} value={query} />
            <Button className="btn-icon" type="submit">
              Cari
            </Button>
          </div>
          <div className="text-muted small">Total data: {totalRows}</div>
        </div>
      </Form>

      <section className="table-panel">
        <Table hover responsive className="data-table">
          <thead>
            <tr>
              <th>Kode User Ecommerce</th>
              <th>Tanggal/Jam Dibuat</th>
              <th>Total Item</th>
              <th>Total Harga</th>
            </tr>
          </thead>
          <tbody>
            {keranjangs.map((keranjang) => (
              <tr
                key={keranjang.id}
                className={`row-clickable ${
                  String(keranjang.id) === String(selectedId) ? "active-row" : ""
                }`}
                onClick={() => navigate(`/keranjang/${keranjang.id}`)}
              >
                <td>{keranjang.visitorId}</td>
                <td>{formatTanggal(keranjang.createdAt)}</td>
                <td>{getTotalQty(keranjang.items)}</td>
                <td>{formatRupiah(getTotalHarga(keranjang.items))}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </section>

      {totalPage > 1 && (
        <Pagination className="mt-3">
          <Pagination.Prev disabled={page === 0} onClick={() => setPage(page - 1)} />
          {Array.from({ length: totalPage }, (_, index) => (
            <Pagination.Item key={index} active={index === page} onClick={() => setPage(index)}>
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next disabled={page + 1 >= totalPage} onClick={() => setPage(page + 1)} />
        </Pagination>
      )}
    </div>
  );
};

export default TampilKeranjang;
